import { createAsyncThunk } from '@reduxjs/toolkit';
import {
	WidgetBundleState,
	CustomWidgetState,
	RecipeWidget,
	WidgetType,
} from '@kemu-io/kemu-core/types';
import {
	getWidgetsInThing,
	getCustomWidgetInnerWidgets,
} from '@src/app/recipe/utils';
import { RootState } from '@src/app/store';
import { safeJsonClone } from '@common/utils';
import { WidgetsMap } from '@src/types/core_t';
import { showCreateSnippetModal } from '@src/features/interface/interfaceSlice';
import { encodeWidgetMapToText } from './clipboardUtils';

type CreateSnippetFromSelectionAction = {
	/** id of the recipe in the pool */
	recipeId: string;
	/** id of the Thing in the recipe */
	thingId: string;
	groupId?: string;
};

/**
 * Creates a snippet from the selected widgets and shows the create snippet modal
 */
export const createSnippetFromSelectionAction = createAsyncThunk('/snippet/createSnippetFromSelection', async (
	payload: CreateSnippetFromSelectionAction,
	thunkApi
): Promise<string> => {
	const { logicMapper } = thunkApi.getState() as RootState;
	const widgetsMap = getWidgetsInThing(payload.recipeId, payload.thingId);
	let widgetsToCloneMap: WidgetsMap = {};

	const selectedWidgetsIds: string[] = [];
	logicMapper.selectedWidgets.forEach((widgetInfo) => {
		const widgetInThing = widgetsMap[widgetInfo.widgetId];
		if (widgetInThing) {
			// Make a copy of the widget without their state's private properties
			const safeWidgetCopy = safeJsonClone<RecipeWidget>(widgetInThing);
			widgetsToCloneMap[widgetInThing.id] = safeWidgetCopy;
			selectedWidgetsIds.push(widgetInThing.id);
		}
	});

	// find group type widgets in the selection and add to the list any inner widgets.
	Object.values(widgetsToCloneMap).forEach((widget) => {
		if (widget.type === WidgetType.widgetGroup) {
			const childrenMap = getCustomWidgetInnerWidgets(
				widget.id,
				payload.thingId,
				payload.recipeId,
				// We need to send the list of selected widgets so that outer children
				// are not removed from the list of children.
				selectedWidgetsIds
			);

			widgetsToCloneMap = {
				...widgetsToCloneMap,
				...childrenMap
			};

			// Update the original reference with the updated fields
			widgetsToCloneMap[widget.id].state = {
				...widget.state,
			};
		}
	});

	// IMPORTANT: Remove children references that are not part of the selection
	// this may happen if the selected widget is linked to a root level widget
	// that is not part of the selection.
	for (const widgetId in widgetsToCloneMap) {
		const widget = widgetsToCloneMap[widgetId];
		widget.children = (widget.children ||[]).filter((child) => {
			return !!widgetsToCloneMap[child.childId];
		});
	}

	// Remove the groupId from any widget at the root level
	if (payload.groupId) {
		Object.values(widgetsToCloneMap).forEach(widget => {
			if (widget.groupId === payload.groupId) {
				widget.groupId = undefined;
			}
		});
	}

	// Generate code representation from the widgets
	const snippetCode = encodeWidgetMapToText(widgetsToCloneMap);
	
	// Show the create snippet modal with the generated code
	thunkApi.dispatch(showCreateSnippetModal({
		snippetCode,
		snippetId: undefined // For new snippets
	}));

	return snippetCode;
});
